/**
 * ============================================================================
 * エリア・店舗選択コンポーネント
 * ============================================================================
 *
 * 対象 DOM: `.c-forms-row-area-shop`
 *
 * 【機能 1】エリア選択に応じて店舗 select の選択肢をフィルタリング
 *   → area-shop-filter.js
 *
 * 【機能 2】「現在地でさがす」ボタン / URL の lat・lng パラメータで最寄り店舗を自動選択
 *   → nearest-shop-finder.js
 *
 * 【機能 3】現在地引き継ぎリンク（[data-nearest-shop-link]）
 *   クリック時に現在地を取得し、href に lat / lng を付与して遷移する
 *   （店舗一覧ページ「現在地でさがす」→ 来店予約ページの導線）
 *   → nearest-shop-link.js
 *
 * 【機能 4】電話案内ブロック（_telephone-inquiry）の電話番号・定休日を選択店舗に連動
 *   → shop-contact-sync.js
 *
 * 【データ受け渡し】
 *   ルート要素の data-* 属性で Blade から JS に値を渡す。
 *   - data-shop-area-map:    { 店舗ID: エリアvalue }
 *   - data-shop-geo-map:     { 店舗ID: { lat: number, lng: number } }
 *   - data-shop-contact-map: { 店舗ID: { tel: string, holiday: string } }
 *
 * 【依存関係】
 *   ・カスタム select コンポーネント（.c-forms-select）の構造に依存
 *     - .c-forms-select__native:   実 select 要素
 *     - .c-forms-select__trigger:  カスタム表示用トリガー
 *     - .c-forms-select__dropdown .c-forms-select__option:  カスタム option
 * ============================================================================
 */

import { parseJsonDataset } from "./utils.js";
import { setupAreaShopFilter } from "./area-shop-filter.js";
import { setupNearestShopFinder } from "./nearest-shop-finder.js";
import { setupNearestShopLinks } from "./nearest-shop-link.js";
import { setupShopContactSync } from "./shop-contact-sync.js";

/**
 * 1 つのルート要素に対して全機能を初期化する。
 * 必須要素（エリア / 店舗 select）が無い場合は何もせず終了。
 *
 * @param {HTMLElement} root - .c-forms-row-area-shop 要素
 * @returns {void}
 */
const initRoot = (root) => {
    // 必須要素を取得
    const areaNative = root.querySelector('.c-forms-select__native[name="area"]');
    const shopNative = root.querySelector('.c-forms-select__native[name="shop_id"]');
    if (!areaNative || !shopNative) return;

    const shopWrapper = shopNative.closest(".c-forms-select");

    // Blade から渡されたデータをパース
    const shopAreaMap = parseJsonDataset(root, "shopAreaMap");
    const shopGeoMap = parseJsonDataset(root, "shopGeoMap");
    const shopContactMap = parseJsonDataset(root, "shopContactMap");

    // 機能 1: エリア → 店舗フィルタ
    setupAreaShopFilter({ shopWrapper, areaNative, shopNative, shopAreaMap });

    // 機能 4: 電話案内ブロックの店舗連動
    // （機能 2 の URL パラメータ自動選択が発火する change イベントを拾えるよう先に登録する）
    setupShopContactSync({ shopNative, shopContactMap });

    // 機能 2: 最寄り店舗検索ボタン
    setupNearestShopFinder({ root, areaNative, shopNative, shopAreaMap, shopGeoMap });
};

/**
 * components/index.js から呼び出されるエントリ関数。
 * ページ内の全ての .c-forms-row-area-shop を初期化する。
 *
 * @returns {void}
 */
const main = () => {
    document.querySelectorAll(".c-forms-row-area-shop").forEach(initRoot);

    // 機能 3: 現在地引き継ぎリンク（コンポーネント外のページにも設置されるため document 全体から検索）
    setupNearestShopLinks();
};

export default main;
